/**
 * Decoration selector - positions, logo upload and colours.
 *
 * A garment can carry up to 3 decorations (positions). Each has a decoration type, a
 * position on the garment, a logo size (the fee product's size boxes), an uploaded logo
 * and its colours. Colours are detected from the uploaded logo as hex codes; the customer
 * can edit or add them, add Pantone references, or ask us to match/choose.
 *
 * Prices come from the fee products: one variant per (size, quantity band). Each position
 * becomes its own fee line in the basket carrying the logo file (Shopify stores it and
 * puts its link on the order) plus position, size and colours. The garment line carries a
 * readable summary and a hidden _decoration_ref shared with its fee lines.
 */
const MAX_POSITIONS = 3;
const MAX_FILE_MB = 20;
const DECORATIONS = [
  { value: 'Print', code: 'print', label: 'Print (DTF)' },
  { value: 'Embroidery - logo on file', code: 'embroidery_existing', label: 'Embroidery - logo already on file' },
  { value: 'Embroidery - new logo', code: 'embroidery_new', label: 'Embroidery - new logo' },
];
// Size keys are the fee products' option-1 values.
const LC = 'Left chest / sleeve';
const POSITIONS = {
  top: [
    { name: 'Left chest', sizes: [LC] },
    { name: 'Right chest', sizes: [LC] },
    { name: 'Left sleeve', sizes: [LC] },
    { name: 'Right sleeve', sizes: [LC] },
    { name: 'Full front', sizes: ['A4', 'A3'] },
    { name: 'Full back', sizes: ['A4', 'A3'] },
    { name: 'Upper back / nape', sizes: ['A6', LC] },
    { name: 'Pocket', sizes: ['A6', LC] },
  ],
  trousers: [
    { name: 'Left hip / thigh', sizes: ['A6', LC] },
    { name: 'Right hip / thigh', sizes: ['A6', LC] },
  ],
  headwear: [
    { name: 'Cap front', sizes: [LC] },
    { name: 'Cap side', sizes: [LC] },
    { name: 'Cap back', sizes: [LC] },
  ],
};
const COLOUR_CHOICES = [
  { value: 'exact', label: 'Use these colours' },
  { value: 'match', label: 'Match my logo as closely as possible' },
  { value: 'choose', label: 'Wrap & Cover to choose' },
];

const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);
const newRef = () => 'D' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 6).toUpperCase();

class DecorationSelectorComponent extends HTMLElement {
  connectedCallback() {
    // Shopify wraps this block in an outer <decoration-selector-component> (the schema "tag").
    // Only the inner element carries the data attributes; the wrapper must do nothing.
    if (!this.dataset.productId || this._ready) return;
    this._ready = true;

    this.productId = this.dataset.productId;
    this.productTitle = this.dataset.productTitle || '';
    this.garmentType = POSITIONS[this.dataset.garmentType] ? this.dataset.garmentType : 'top';
    this.digitisingFeeVariantId = this.dataset.digitisingFeeVariantId;
    this.digitisingFeeCents = parseInt(this.dataset.digitisingFeeCents, 10) || 0;
    this.fallback = {
      print: { id: this.dataset.printFeeVariantId, cents: parseInt(this.dataset.printSurchargeCents, 10) || 0 },
      embroidery: { id: this.dataset.embroideryFeeVariantId, cents: parseInt(this.dataset.embroiderySurchargeCents, 10) || 0 },
    };
    this.printVariants = this._parseJson(this.dataset.printVariants) || [];
    this.embroideryVariants = this._parseJson(this.dataset.embroideryVariants) || [];
    this.printPricing = this._parseJson(this.dataset.printPricing) || null;
    this.embroideryPricing = this._parseJson(this.dataset.embroideryPricing) || null;
    this.isChildrenswear = this.dataset.isChildrenswear === 'true';

    this.rowsEl = this.querySelector('[ref="rows"]');
    this.addBtn = this.querySelector('[ref="addPosition"]');
    this.totalEl = this.querySelector('[ref="decorationTotal"]');
    this.totalValueEl = this.querySelector('[ref="decorationTotalValue"]');
    this.summaryInput = this.querySelector('[ref="summaryInput"]');
    this.refInput = this.querySelector('[ref="refInput"]');

    this.positions = [this._blankPosition()];
    this._gridQuantity = 0;
    this._pendingSubmission = null;
    this.ref = newRef();

    this._onGridQuantityChanged = (event) => {
      if (!this._matchesThisProduct(event)) return;
      this._gridQuantity = parseInt(event?.detail?.quantity, 10) || 0;
      this.updatePriceDisplay();
    };
    this._onFormSubmitCapture = (event) => this._captureSubmission(event);
    this._onCartUpdate = (event) => this._handleCartUpdate(event);
    this._onExternalSubmission = (event) => this._registerExternalSubmission(event);
    this._onRowsChange = (event) => this._handleRowEvent(event);
    this._onRowsClick = (event) => this._handleRowClick(event);
    this._onAdd = () => {
      if (this.positions.length >= MAX_POSITIONS) return;
      this.positions.push(this._blankPosition('print'));
      this.render();
    };

    document.addEventListener('decoration:quantity-changed', this._onGridQuantityChanged);
    document.addEventListener('cart:update', this._onCartUpdate);
    document.addEventListener('decoration:register-submission', this._onExternalSubmission);
    this.rowsEl?.addEventListener('change', this._onRowsChange);
    this.rowsEl?.addEventListener('input', this._onRowsChange);
    this.rowsEl?.addEventListener('click', this._onRowsClick);
    this.addBtn?.addEventListener('click', this._onAdd);
    this._productForm = this._findProductForm();
    this._productForm?.addEventListener('submit', this._onFormSubmitCapture, { capture: true });

    this.render();
  }

  disconnectedCallback() {
    if (!this._ready) return;
    document.removeEventListener('decoration:quantity-changed', this._onGridQuantityChanged);
    document.removeEventListener('cart:update', this._onCartUpdate);
    document.removeEventListener('decoration:register-submission', this._onExternalSubmission);
    this._productForm?.removeEventListener('submit', this._onFormSubmitCapture, { capture: true });
    this._ready = false;
  }

  // ---------------------------------------------------------------- data helpers
  _parseJson(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error('Decoration selector: could not parse data attribute', error);
      return null;
    }
  }

  _matchesThisProduct(event) {
    const productId = event?.detail?.data?.productId ?? event?.detail?.productId;
    return !productId || String(productId) === String(this.productId);
  }

  _findProductForm() {
    return this.closest('.shopify-section')?.querySelector('form[data-type="add-to-cart-form"]') || null;
  }

  _blankPosition(decoration = 'none') {
    const pos = { decoration, position: null, sizeKey: null, file: null, preview: null, colours: [], detected: false,
                  pantone: '', choice: 'exact', note: '' };
    this._normalise(pos);
    return pos;
  }

  _decoration(code) {
    return DECORATIONS.find((d) => d.code === code) || null;
  }

  _isEmbroidery(code) {
    return code === 'embroidery_new' || code === 'embroidery_existing';
  }

  _variantsFor(code) {
    if (code === 'print') return this.printVariants;
    if (this._isEmbroidery(code)) return this.embroideryVariants;
    return [];
  }

  _pricingFor(code) {
    if (code === 'print') return this.printPricing;
    if (this._isEmbroidery(code)) return this.embroideryPricing;
    return null;
  }

  _sizeEntry(code, sizeKey) {
    const pricing = this._pricingFor(code);
    if (!pricing || !sizeKey) return null;
    return (pricing.sizes || []).find((s) => s.option_value === sizeKey || s.label === sizeKey) || null;
  }

  _sizeLabel(code, sizeKey) {
    const entry = this._sizeEntry(code, sizeKey);
    if (!entry) return sizeKey;
    const dims = entry.width_mm && entry.height_mm ? ` · ${entry.width_mm} × ${entry.height_mm} mm` : '';
    return `${entry.label || sizeKey}${dims}`;
  }

  /** Sizes that exist for this decoration, suit the position and suit the garment. */
  _sizesFor(code, positionName) {
    const offered = [];
    for (const v of this._variantsFor(code)) if (v.size && !offered.includes(v.size)) offered.push(v.size);
    const pos = POSITIONS[this.garmentType].find((p) => p.name === positionName);
    let sizes = pos ? pos.sizes.filter((s) => offered.includes(s)) : offered;
    sizes = sizes.filter((s) => !(this._sizeEntry(code, s)?.adults_only && this.isChildrenswear));
    return sizes;
  }

  _positionsFor(code) {
    return POSITIONS[this.garmentType].filter((p) => this._sizesFor(code, p.name).length > 0);
  }

  /** Keep a position's choices valid after the decoration or position changes. */
  _normalise(pos) {
    if (pos.decoration === 'none') return;
    const options = this._positionsFor(pos.decoration);
    const taken = this.positions ? this.positions.filter((p) => p !== pos).map((p) => p.position) : [];
    if (!options.some((p) => p.name === pos.position)) {
      pos.position = (options.find((p) => !taken.includes(p.name)) || options[0] || {}).name || null;
    }
    const sizes = this._sizesFor(pos.decoration, pos.position);
    if (!sizes.includes(pos.sizeKey)) pos.sizeKey = sizes[0] || null;
  }

  _bandFor(quantity) {
    const qty = quantity > 0 ? quantity : 1;
    if (qty <= 10) return '1-10';
    if (qty <= 49) return '11-49';
    if (qty <= 99) return '50-99';
    if (qty <= 249) return '100-249';
    return '250+';
  }

  _resolveFee(pos, quantity) {
    if (pos.decoration === 'none') return null;
    const band = this._bandFor(quantity);
    const match = this._variantsFor(pos.decoration).find((v) => v.size === pos.sizeKey && v.band === band);
    if (match) return { id: String(match.id), cents: match.price, exact: true };
    const fb = this.fallback[pos.decoration === 'print' ? 'print' : 'embroidery'];
    return fb.id ? { id: String(fb.id), cents: fb.cents, exact: false } : null;
  }

  _activePositions(list = this.positions) {
    return list.filter((p) => p.decoration !== 'none');
  }

  _logoKey(pos) {
    return pos.file ? `${pos.file.name}|${pos.file.size}` : `pos:${this.positions.indexOf(pos)}`;
  }

  _formatMoney(cents) {
    return '£' + (cents / 100).toFixed(2);
  }

  // ---------------------------------------------------------------- rendering
  render() {
    const rows = this.positions.map((pos, i) => this._rowHtml(pos, i)).join('');
    this.rowsEl.innerHTML = rows;
    const active = this._activePositions().length;
    this.addBtn.hidden = active === 0 || this.positions.length >= MAX_POSITIONS;
    this._updateHidden();
    this.updatePriceDisplay();
  }

  _rowHtml(pos, i) {
    const first = i === 0;
    const decoOptions = (first ? [{ code: 'none', value: 'No decoration', label: 'No decoration' }] : []).concat(DECORATIONS)
      .map((d) => `<option value="${d.code}" ${d.code === pos.decoration ? 'selected' : ''}>${esc(d.label)}</option>`).join('');
    let html = `<div class="decoration-selector__row" data-i="${i}">
      ${this.positions.length > 1 || i > 0 ? `<div class="decoration-selector__row-head"><span>Position ${i + 1}</span>
        <button type="button" class="decoration-selector__remove" data-act="remove">Remove</button></div>` : ''}
      <div class="decoration-selector__field">
        <label class="decoration-selector__label">Decoration</label>
        <select class="field__input decoration-selector__select" data-f="decoration">${decoOptions}</select>
      </div>`;
    if (pos.decoration === 'none') return html + '</div>';

    const positions = this._positionsFor(pos.decoration)
      .map((p) => `<option ${p.name === pos.position ? 'selected' : ''}>${esc(p.name)}</option>`).join('');
    const sizes = this._sizesFor(pos.decoration, pos.position)
      .map((s) => `<option value="${esc(s)}" ${s === pos.sizeKey ? 'selected' : ''}>${esc(this._sizeLabel(pos.decoration, s))}</option>`).join('');
    const entry = this._sizeEntry(pos.decoration, pos.sizeKey);
    const pricing = this._pricingFor(pos.decoration);
    const notes = [];
    if (entry?.stitch_cap) {
      notes.push(`Covers up to ${entry.stitch_cap.toLocaleString('en-GB')} stitches.`);
      if (pricing?.over_cap_per_1000_stitches) notes.push(`Over that, £${pricing.over_cap_per_1000_stitches.toFixed(2)} per extra 1,000.`);
    }
    if (entry?.note) notes.push(entry.note);
    else if (entry?.quotation_required) notes.push('Confirmed by quotation against the garment.');
    const needsLogo = pos.decoration !== 'embroidery_existing';

    html += `
      <div class="decoration-selector__field">
        <label class="decoration-selector__label">Position</label>
        <select class="field__input decoration-selector__select" data-f="position">${positions}</select>
      </div>
      <div class="decoration-selector__field">
        <label class="decoration-selector__label">Logo size</label>
        <select class="field__input decoration-selector__select" data-f="size">${sizes}</select>
        ${notes.length ? `<p class="decoration-selector__hint">${esc(notes.join(' '))}</p>` : ''}
      </div>
      <div class="decoration-selector__field">
        <label class="decoration-selector__label">Your logo${needsLogo ? '' : ' (optional - we have it on file)'}</label>
        <div class="decoration-selector__upload">
          ${pos.preview ? `<img class="decoration-selector__thumb" src="${pos.preview}" alt="">` : ''}
          <label class="decoration-selector__upload-button">${pos.file ? 'Change file' : 'Upload logo'}
            <input type="file" data-f="file" accept="image/*,.pdf,.ai,.eps,.svg,.psd,.tif,.tiff"></label>
          <span class="decoration-selector__hint">${pos.file ? esc(pos.file.name) : 'PNG, JPG, PDF, AI, EPS or SVG - up to ' + MAX_FILE_MB + ' MB'}</span>
        </div>
        ${needsLogo && !pos.file ? `<p class="decoration-selector__hint">No logo yet? You can still order - we'll email you for it.</p>` : ''}
        ${pos.note ? `<p class="decoration-selector__warn">${esc(pos.note)}</p>` : ''}
      </div>
      <div class="decoration-selector__field">
        <label class="decoration-selector__label">Logo colours</label>
        ${pos.detected ? `<p class="decoration-selector__hint">Detected from your logo - tap a colour to change it.</p>` : ''}
        <div class="decoration-selector__swatches">
          ${pos.colours.map((hex, ci) => `<span class="decoration-selector__swatch">
              <input type="color" value="${hex}" data-f="colour" data-ci="${ci}" aria-label="Colour ${ci + 1}">${hex.toUpperCase()}
              <button type="button" data-act="rmcolour" data-ci="${ci}" aria-label="Remove colour">×</button></span>`).join('')}
          ${pos.colours.length < 8 ? `<button type="button" class="decoration-selector__remove" data-act="addcolour">+ Add colour</button>` : ''}
        </div>
        <input class="field__input decoration-selector__text" data-f="pantone" value="${esc(pos.pantone)}"
          placeholder="Pantone or colour notes (optional), e.g. PMS 286 C">
        <div class="decoration-selector__choice">
          ${COLOUR_CHOICES.map((c) => `<label><input type="radio" name="dcc-${this.productId}-${i}" data-f="choice" value="${c.value}" ${pos.choice === c.value ? 'checked' : ''}> ${esc(c.label)}</label>`).join('')}
        </div>
        ${pos.decoration === 'print' ? `<p class="decoration-selector__hint">DTF prints in full colour (CMYK) - Pantone references are matched as closely as possible.</p>` : ''}
      </div>
    </div>`;
    return html;
  }

  _updateHidden() {
    const active = this._activePositions();
    const summary = active.length
      ? active.map((p) => `${this._decoration(p.decoration).value}: ${p.position} (${this._sizeLabel(p.decoration, p.sizeKey)})`).join('; ')
      : 'No decoration';
    if (this.summaryInput) this.summaryInput.value = summary;
    if (this.refInput) this.refInput.value = active.length ? this.ref : '';
  }

  updatePriceDisplay() {
    const active = this._activePositions();
    const quantity = this._gridQuantity;
    if (!active.length || quantity <= 0) {
      if (this.totalEl) this.totalEl.hidden = true;
      return;
    }
    let total = 0;
    const parts = [];
    const logos = new Set();
    for (const pos of active) {
      const fee = this._resolveFee(pos, quantity);
      if (!fee) continue;
      total += fee.cents * quantity;
      parts.push(`${pos.position} ${this._formatMoney(fee.cents)} each`);
      if (pos.decoration === 'embroidery_new') logos.add(this._logoKey(pos));
    }
    total += logos.size * this.digitisingFeeCents;
    const itemWord = quantity === 1 ? 'item' : 'items';
    let text = `+ ${this._formatMoney(total)} decoration for ${quantity} ${itemWord}`;
    if (parts.length) text += ` <span class="decoration-selector__saving">(${esc(parts.join(', '))}` +
      `${logos.size ? `, + ${this._formatMoney(logos.size * this.digitisingFeeCents)} digitising` : ''})</span>`;
    this.totalValueEl.innerHTML = text;
    this.totalEl.hidden = false;
  }

  // ---------------------------------------------------------------- editing
  _rowOf(el) {
    const row = el.closest('[data-i]');
    return row ? this.positions[parseInt(row.dataset.i, 10)] : null;
  }

  _handleRowEvent(event) {
    const el = event.target;
    const pos = this._rowOf(el);
    const f = el.dataset.f;
    if (!pos || !f) return;
    if (f === 'colour') {
      pos.colours[parseInt(el.dataset.ci, 10)] = el.value;
      if (el.nextSibling) el.nextSibling.textContent = el.value.toUpperCase();  // the hex label beside the swatch
      return;
    }
    if (f === 'pantone') { pos.pantone = el.value; return; }
    if (event.type === 'input') return;  // everything else re-renders once, on 'change'
    if (f === 'decoration') { pos.decoration = el.value; this._normalise(pos); }
    else if (f === 'position') { pos.position = el.value; this._normalise(pos); }
    else if (f === 'size') pos.sizeKey = el.value;
    else if (f === 'choice') pos.choice = el.value;
    else if (f === 'file') { this._setFile(pos, el.files && el.files[0]); return; }
    if (pos.decoration === 'none') this.positions = [pos];  // removing all decoration clears extra positions
    this.render();
  }

  _handleRowClick(event) {
    const btn = event.target.closest('[data-act]');
    if (!btn) return;
    const pos = this._rowOf(btn);
    if (!pos) return;
    const act = btn.dataset.act;
    if (act === 'remove') {
      this.positions.splice(this.positions.indexOf(pos), 1);
      if (!this.positions.length) this.positions = [this._blankPosition()];
    } else if (act === 'addcolour') {
      pos.colours.push('#000000');
    } else if (act === 'rmcolour') {
      pos.colours.splice(parseInt(btn.dataset.ci, 10), 1);
    } else return;
    this.render();
  }

  async _setFile(pos, file) {
    pos.note = '';
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      pos.note = `That file is over ${MAX_FILE_MB} MB - please send a smaller version or email it to us.`;
      this.render();
      return;
    }
    pos.file = file;
    if (pos.preview) URL.revokeObjectURL(pos.preview);
    pos.preview = /^image\/(png|jpe?g|gif|webp|svg\+xml|bmp)$/.test(file.type) ? URL.createObjectURL(file) : null;
    const colours = await DecorationSelectorComponent.extractColours(file).catch(() => null);
    if (colours && colours.length) {
      pos.colours = colours;
      pos.detected = true;
    } else {
      pos.detected = false;
      if (!pos.preview) pos.note = "We'll read the colours from your file - add them below if you know them.";
    }
    this.render();
  }

  /** Main colours of a logo as hex codes (background and anti-aliasing ignored). */
  static async extractColours(file, maxColours = 6) {
    const isImage = /^image\/(png|jpe?g|gif|webp|svg\+xml|bmp)$/.test(file.type) || /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(file.name);
    if (!isImage) return null;
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = url;
      });
      const nw = img.naturalWidth || 300, nh = img.naturalHeight || 300;
      const s = Math.min(1, 160 / Math.max(nw, nh));
      const w = Math.max(1, Math.round(nw * s)), h = Math.max(1, Math.round(nh * s));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      const data = ctx.getImageData(0, 0, w, h).data;
      const n = w * h;
      let transparent = 0;
      for (let i = 0; i < n; i++) if (data[i * 4 + 3] < 250) transparent++;
      const hasAlpha = transparent > n * 0.05;
      // background = most common border colour when the image has no transparency
      let bg = null;
      if (!hasAlpha) {
        const counts = new Map();
        const add = (x, y) => {
          const k = (y * w + x) * 4;
          const key = ((data[k] >> 3) << 10) | ((data[k + 1] >> 3) << 5) | (data[k + 2] >> 3);
          counts.set(key, (counts.get(key) || 0) + 1);
        };
        for (let x = 0; x < w; x++) { add(x, 0); add(x, h - 1); }
        for (let y = 0; y < h; y++) { add(0, y); add(w - 1, y); }
        const [key] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
        bg = [((key >> 10) & 31) * 8 + 4, ((key >> 5) & 31) * 8 + 4, (key & 31) * 8 + 4];
      }
      const px = [];
      for (let i = 0; i < n; i++) {
        const k = i * 4;
        if (data[k + 3] < 200) continue;
        const r = data[k], g = data[k + 1], b = data[k + 2];
        if (bg && (r - bg[0]) ** 2 + (g - bg[1]) ** 2 + (b - bg[2]) ** 2 < 32 * 32) continue;
        px.push([r, g, b]);
      }
      if (px.length < 10) return null;
      // seeds: most common coarse colours, then k-means
      const buckets = new Map();
      for (const p of px) {
        const key = ((p[0] >> 4) << 8) | ((p[1] >> 4) << 4) | (p[2] >> 4);
        const b = buckets.get(key) || { n: 0, s: [0, 0, 0] };
        b.n++; b.s[0] += p[0]; b.s[1] += p[1]; b.s[2] += p[2];
        buckets.set(key, b);
      }
      let centres = [...buckets.values()].sort((a, b) => b.n - a.n).slice(0, 10).map((b) => b.s.map((v) => v / b.n));
      let sizes = [];
      for (let iter = 0; iter < 8; iter++) {
        const sums = centres.map(() => [0, 0, 0]);
        sizes = centres.map(() => 0);
        for (const p of px) {
          let best = 0, bd = Infinity;
          for (let c = 0; c < centres.length; c++) {
            const d = (p[0] - centres[c][0]) ** 2 + (p[1] - centres[c][1]) ** 2 + (p[2] - centres[c][2]) ** 2;
            if (d < bd) { bd = d; best = c; }
          }
          sums[best][0] += p[0]; sums[best][1] += p[1]; sums[best][2] += p[2]; sizes[best]++;
        }
        centres = centres.map((c, i) => (sizes[i] ? sums[i].map((v) => v / sizes[i]) : c));
      }
      // merge near-identical colours, drop tiny ones (edge blending)
      const found = [];
      centres.map((c, i) => ({ c, n: sizes[i] })).sort((a, b) => b.n - a.n).forEach(({ c, n: count }) => {
        if (count < px.length * 0.03) return;
        const near = found.find((f) => (f.c[0] - c[0]) ** 2 + (f.c[1] - c[1]) ** 2 + (f.c[2] - c[2]) ** 2 < 30 * 30);
        if (near) near.n += count;
        else found.push({ c, n: count });
      });
      const hex = (c) => '#' + c.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('');
      return found.sort((a, b) => b.n - a.n).slice(0, maxColours).map((f) => hex(f.c));
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  // ---------------------------------------------------------------- adding to the basket
  _snapshot(quantity) {
    this._updateHidden();
    return {
      positions: this._activePositions().map((p) => ({ ...p, colours: [...p.colours] })),
      ref: this.ref,
      quantity,
      timestamp: Date.now(),
    };
  }

  _captureSubmission(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const quantity = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;
    this._pendingSubmission = this._snapshot(quantity);
  }

  _registerExternalSubmission(event) {
    const { quantity, productId } = event?.detail || {};
    if (productId != null && String(productId) !== String(this.productId)) return;
    this._pendingSubmission = this._snapshot(parseInt(quantity, 10) || 1);
  }

  async _handleCartUpdate(event) {
    const data = event?.detail?.data;
    if (!data || data.source === 'decoration-fee-addon' || data.didError) return;
    if (!this._matchesThisProduct(event)) return;
    const sub = this._pendingSubmission;
    this._pendingSubmission = null;
    if (!sub || Date.now() - sub.timestamp > 15000 || !sub.positions.length) return;

    const failures = [];
    const charged = await this._digitisedLogos();
    const digitise = new Map();
    for (const pos of sub.positions) {
      const fee = this._resolveFee(pos, sub.quantity);
      if (!fee) continue;
      const deco = this._decoration(pos.decoration);
      const fd = new FormData();
      fd.append('id', fee.id);
      fd.append('quantity', String(sub.quantity));
      fd.append('properties[Position]', pos.position);
      fd.append('properties[Logo size]', this._sizeLabel(pos.decoration, pos.sizeKey));
      fd.append('properties[Decoration]', deco.value);
      fd.append('properties[Garment]', this.productTitle);
      if (pos.file) {
        fd.append('properties[Logo]', pos.file, pos.file.name);
        fd.append('properties[Logo file name]', pos.file.name);
      } else if (pos.decoration !== 'embroidery_existing') {
        fd.append('properties[Logo]', 'To follow - please email your logo');
      }
      if (pos.colours.length) fd.append('properties[Colours (hex)]', pos.colours.map((c) => c.toUpperCase()).join(', '));
      if (pos.pantone.trim()) fd.append('properties[Pantone / colour notes]', pos.pantone.trim());
      fd.append('properties[Colour instructions]', COLOUR_CHOICES.find((c) => c.value === pos.choice).label);
      fd.append('properties[_decoration_ref]', sub.ref);
      try {
        const res = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
        if (!res.ok) failures.push(`${pos.position}: ${(await res.json().catch(() => ({}))).description || res.status}`);
      } catch (error) {
        failures.push(`${pos.position}: ${error.message}`);
      }
      if (pos.decoration === 'embroidery_new') {
        const key = pos.file ? pos.file.name : `${pos.position} logo`;
        if (!charged.has(key)) digitise.set(key, pos);
      }
    }
    if (this.digitisingFeeVariantId && digitise.size) {
      const items = [...digitise.keys()].map((key) => ({
        id: Number(this.digitisingFeeVariantId), quantity: 1,
        properties: { 'Logo file name': key, _source_product_id: String(this.productId), _decoration_ref: sub.ref },
      }));
      try {
        await fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
      } catch (error) {
        failures.push(`digitising: ${error.message}`);
      }
    }
    if (failures.length) {
      console.error('Decoration fee item(s) failed to add to cart', failures);
      alert('Your garments were added, but a decoration could not be added: ' + failures.join('; ') +
            '. Please try again or contact us.');
    }
    this.ref = newRef();  // the next add gets its own link between garments and decoration
    this._updateHidden();
    const cart = await (await fetch('/cart.js')).json();
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: { resource: cart, sourceId: this.id || 'decoration-selector', data: { source: 'decoration-fee-addon', productId: this.productId } },
    }));
  }

  /** Logo file names already charged a digitising fee in this basket. */
  async _digitisedLogos() {
    const out = new Set();
    if (!this.digitisingFeeVariantId) return out;
    try {
      const cart = await (await fetch('/cart.js')).json();
      for (const item of cart.items) {
        if (String(item.variant_id) === String(this.digitisingFeeVariantId)) out.add(item.properties?.['Logo file name'] || '');
      }
    } catch (error) {
      console.error('Failed to check cart for digitising fees', error);
    }
    return out;
  }
}

customElements.define('decoration-selector-component', DecorationSelectorComponent);
